import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { closeAllModals } from 'reducers/modalReducer';
import { LoadingButton } from '@mui/lab';
import { useCallBrBusinessEvent } from 'hooks/queries/useDisableBr';
import { JBColors, JBIcon } from '@bjb-gcrm/gcrm-frontend-components';
import { kebabToTitleCase } from 'utils/stringUtil';
import { BrBusinessEvents } from 'types/BrTypes';

const RejectWithdrawBrModal = ({ brId, modalPurpose }: { brId: string; modalPurpose: 'REJECT' | 'WITHDRAW' }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const rejectWithdraw = modalPurpose.toLowerCase();
  const disableBr = useCallBrBusinessEvent(brId);

  const businessEvent = modalPurpose === 'REJECT' ? BrBusinessEvents.BR_REJECTED : BrBusinessEvents.BR_WITHDRAWN;

  return (
    <Dialog
      id="reject-withdraw-dialog"
      data-testid="reject-withdraw-dialog"
      maxWidth="sm"
      open
      fullWidth={true}
      aria-labelledby="dialog-title"
      onClose={() => dispatch(closeAllModals())}
    >
      <DialogContent>
        <DialogTitle id="reject-withdraw-modal-title" sx={{ padding: 0 }}>
          <Grid container justifyContent="space-between">
            <Grid item container xs={11} alignItems="center">
              <JBIcon iconName="dangerOutline" size="xl" fill={JBColors.support.orange[120]} />
              <Typography variant="h5" display="inline" ml="16px" color={JBColors.support.orange[120]}>
                {`${kebabToTitleCase(rejectWithdraw)} confirmation`}
              </Typography>
            </Grid>

            <Grid item xs={1}>
              <IconButton onClick={() => dispatch(closeAllModals())}>
                <JBIcon iconName="close" fill={JBColors.support.orange[120]} size="xl" />
              </IconButton>
            </Grid>
          </Grid>
          <Divider sx={{ borderColor: JBColors.support.orange[120] }} />
        </DialogTitle>

        <Grid container>
          <Grid item xs={12} sx={{ marginTop: '12px' }}>
            <Typography>
              {t('accounts.tabs.more.withdraw.modal.title.confirmText', {
                purpose: rejectWithdraw,
              })}
            </Typography>
          </Grid>
        </Grid>

        <DialogActions sx={{ marginTop: '12px', paddingRight: 0 }}>
          <Button
            onClick={() => dispatch(closeAllModals())}
            color="primary"
            id="reject-withdraw-br-cancel-button"
            disabled={disableBr.isPending}
          >
            Cancel
          </Button>

          <LoadingButton
            variant="contained"
            color="primary"
            id="reject-withdraw-br-button"
            type="submit"
            sx={{ margin: 0 }}
            disabled={disableBr.isPending}
            loading={disableBr.isPending}
            onClick={() => disableBr.mutate({ brUid: brId, businessEvent })}
          >
            {`${kebabToTitleCase(rejectWithdraw)} BR`}
          </LoadingButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default RejectWithdrawBrModal;
